import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { formatDate } from "../dashboardHelpers";

function formatDateTime(iso) {
  if (!iso) return "";
  const date = formatDate(iso);
  try {
    const time = new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${date}, ${time}`;
  } catch {
    return date;
  }
}

function StatTile({ label, value, highlight }) {
  return (
    <Box flex="1 1 0" minWidth={72}>
      <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem" }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, color: highlight ? "primary.main" : "text.primary", lineHeight: 1.3 }}>
        {value}
      </Typography>
    </Box>
  );
}

// A refund gives a session back, so it reads as +1 to the client even though the
// row's credit is negative — the sign is about the package, not the balance.
function CreditChip({ credits }) {
  const isRefund = credits < 0;
  return (
    <Chip
      size="small"
      label={isRefund ? `+${Math.abs(credits)}` : `−${credits}`}
      color={isRefund ? "success" : "default"}
      variant="outlined"
      sx={{ height: "1.25rem", fontSize: "0.7rem", fontWeight: 600 }}
    />
  );
}

function UsageRow({ row, isMobile }) {
  const meta = [row.serviceName, row.trainerName].filter(Boolean).join(" • ");

  if (isMobile) {
    return (
      <Box py={1.25}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={600}>
              {formatDateTime(row.bookingDate)}
            </Typography>
            {meta && (
              <Typography variant="body2" color="textSecondary" sx={{ wordBreak: "break-word" }}>
                {meta}
              </Typography>
            )}
          </Box>
          <CreditChip credits={row.creditsUsed} />
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={2} py={1}>
      <Typography variant="body2" sx={{ flex: "0 0 40%" }} noWrap>
        {formatDateTime(row.bookingDate)}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {meta}
      </Typography>
      <CreditChip credits={row.creditsUsed} />
    </Box>
  );
}

function PlanUsageDialog({ state, onClose }) {
  const { open, data, loading, error } = state;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const summary = data?.summary;
  const usage = data?.usage || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box minWidth={0}>
          <Typography variant="h6" component="span" noWrap display="block">
            {data?.plan?.name || "Package usage"}
          </Typography>
          {summary?.purchaseDate && (
            <Typography variant="caption" display="block" color="textSecondary">
              Purchased {formatDate(summary.purchaseDate)}
            </Typography>
          )}
        </Box>
        {data?.plan?.status && (
          <Chip
            size="small"
            label={data.plan.status}
            color={data.plan.status === "active" ? "success" : "default"}
            variant="outlined"
            sx={{ height: "1.375rem", fontSize: "0.7rem", fontWeight: 500, letterSpacing: 0.3 }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : error || !data ? (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
            {error || "This package's usage isn't available."}
          </Typography>
        ) : (
          <>
            <Box display="flex" gap={2} flexWrap="wrap" pb={2}>
              <StatTile label="Total" value={summary?.totalSessions ?? "—"} />
              <StatTile label="Used" value={summary?.sessionsConsumed ?? 0} />
              <StatTile label="Refunded" value={summary?.sessionsRefunded ?? 0} />
              <StatTile label="Remaining" value={summary?.remainingSessions ?? 0} highlight />
            </Box>

            <Divider />

            <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block", pt: 2 }}>
              Sessions
            </Typography>

            {usage.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ py: 3 }} align="center">
                No sessions used yet.
              </Typography>
            ) : (
              <Box>
                {usage.map((row, i) => (
                  <React.Fragment key={`${row.bookingDate}-${i}`}>
                    {i > 0 && <Divider />}
                    <UsageRow row={row} isMobile={isMobile} />
                  </React.Fragment>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PlanUsageDialog;
